import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Flame, Check, Plus, X } from "lucide-react";
import api, { formatError, track } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { DatePicker } from "./DatePicker";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Progress } from "./ui/progress";

const STATIONS = ["ski erg", "sled push", "sled pull", "burpee broad jump", "rowing", "farmer's carry", "sandbag lunges", "wall balls"];
const ALLERGENS = ["gluten", "lactose", "fruits à coque", "œufs", "poisson", "crustacés", "soja", "arachide"];
const DAYS = ["Lundi", "Mardi", "Mercredi", "Jeudi", "Vendredi", "Samedi", "Dimanche"];
const LEVELS = ["débutant", "intermédiaire", "avancé", "élite"];
const TARGET_TIMES = ["Moins de 1h00", "1h00–1h10", "1h10–1h20", "1h20–1h30", "1h30–1h45", "Plus de 1h45", "Découverte / finir la course"];

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-widest text-muted-foreground">{label}</Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}

function Chips({ options, value, onToggle, testid }) {
  return (
    <div className="flex flex-wrap gap-2" data-testid={testid}>
      {options.map((o) => {
        const active = value.includes(o);
        return (
          <button key={o} type="button" onClick={() => onToggle(o)}
            className={`px-4 h-11 rounded-md border text-sm font-medium capitalize transition-colors active:scale-95 ${active ? "bg-primary border-primary text-white" : "bg-secondary border-border text-foreground hover:border-primary/50"}`}>
            {o}
          </button>
        );
      })}
    </div>
  );
}

export default function Onboarding() {
  const nav = useNavigate();
  const { refreshUser } = useAuth();
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [f, setF] = useState({
    height_cm: "", weight_kg: "", age: "", level: "intermédiaire",
    target_time: "", race_date: "", category: "open", gender: "homme",
    pb_1km: "", pb_5km: "", pb_10km: "", weekly_run_km: "",
    weak_stations: [], wall_balls_unbroken: "", wall_balls_weight: "",
    available_days: [], time_slots: [], availability: [], weekly_hours: "",
    diet: "", restrictions: "", supplements: "", sleep_hours: "", injuries: "",
    allergies: [], allergies_other: "", diet_type: "omnivore", liked_foods: "", disliked_foods: "", about_me: "",
  });

  useEffect(() => {
    api.get("/profile").then((r) => { if (r.data && Object.keys(r.data).length) setF((p) => ({ ...p, ...r.data })); }).catch(() => {});
  }, []);

  useEffect(() => { track("onboarding_step", { step }); }, [step]);

  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  const setNum = (k) => (e) => { const v = e.target.value; if (v === "" || Number(v) >= 0) setF((p) => ({ ...p, [k]: v })); };
  const toggle = (k, v) => setF((p) => ({ ...p, [k]: p[k].includes(v) ? p[k].filter((x) => x !== v) : [...p[k], v] }));

  const toggleDay = (day) => setF((p) => {
    const exists = p.availability.find((a) => a.day === day);
    const availability = exists
      ? p.availability.filter((a) => a.day !== day)
      : [...p.availability, { day, ranges: [{ start: "18:00", end: "20:00" }] }];
    return { ...p, availability };
  });
  const updateRange = (day, idx, field, val) => setF((p) => ({
    ...p,
    availability: p.availability.map((a) => a.day === day
      ? { ...a, ranges: a.ranges.map((r, i) => (i === idx ? { ...r, [field]: val } : r)) } : a),
  }));
  const addRange = (day) => setF((p) => ({
    ...p,
    availability: p.availability.map((a) => (a.day === day && a.ranges.length < 2)
      ? { ...a, ranges: [...a.ranges, { start: "07:00", end: "08:30" }] } : a),
  }));
  const removeRange = (day, idx) => setF((p) => ({
    ...p,
    availability: p.availability.map((a) => a.day === day
      ? { ...a, ranges: a.ranges.filter((_, i) => i !== idx) } : a),
  }));

  const steps = ["Physio & Objectif", "Historique & Faiblesses", "Disponibilité", "Nutrition & Santé"];

  const submit = async () => {
    setSaving(true);
    try {
      const payload = { ...f };
      ["height_cm", "weight_kg", "age", "weekly_run_km", "wall_balls_unbroken", "weekly_hours", "sleep_hours"].forEach((k) => {
        payload[k] = payload[k] === "" ? null : Number(payload[k]);
      });
      payload.available_days = f.availability.map((a) => a.day);
      await api.post("/profile", payload);
      track("onboarding_completed", {});
      await refreshUser();
      toast.success("Profil enregistré !");
      try { await api.post("/programs/generate", {}); } catch (e) { /* handled on /app */ }
      nav("/app");
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-background px-5 py-8 md:py-12">
      <div className="max-w-2xl mx-auto">
        <div className="flex items-center gap-2 mb-6">
          <Flame className="text-primary" size={24} strokeWidth={2.5} />
          <span className="font-display text-2xl">ROX<span className="text-primary">LAB</span></span>
        </div>
        <p className="text-xs uppercase tracking-[0.2em] text-primary mb-2">Étape {step + 1} / 4 — {steps[step]}</p>
        <Progress value={((step + 1) / 4) * 100} className="h-2 mb-8 bg-secondary" />

        <div className="bg-card border border-border rounded-lg p-6 space-y-5 fade-up" key={step}>
          {step === 0 && (
            <>
              <div className="grid grid-cols-2 gap-4">
                <Field label="Taille (cm)"><Input data-testid="ob-height" type="number" min="0" value={f.height_cm} onChange={setNum("height_cm")} className="h-12 text-base bg-secondary border-border" /></Field>
                <Field label="Poids (kg)"><Input data-testid="ob-weight" type="number" min="0" value={f.weight_kg} onChange={setNum("weight_kg")} className="h-12 text-base bg-secondary border-border" /></Field>
                <Field label="Âge"><Input data-testid="ob-age" type="number" min="0" value={f.age} onChange={setNum("age")} className="h-12 text-base bg-secondary border-border" /></Field>
              </div>
              <Field label="Niveau"><Chips testid="ob-level" options={LEVELS} value={[f.level]} onToggle={(v) => setF((p) => ({ ...p, level: v }))} /></Field>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label="Temps cible"><Chips testid="ob-target" options={TARGET_TIMES} value={[f.target_time]} onToggle={(v) => setF((p) => ({ ...p, target_time: v }))} /></Field>
                <Field label="Date de course"><DatePicker testid="ob-racedate" value={f.race_date} onChange={(v) => setF((p) => ({ ...p, race_date: v }))} placeholder="Choisir la date" /></Field>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <Field label="Catégorie"><Chips testid="ob-cat" options={["open", "pro"]} value={[f.category]} onToggle={(v) => setF((p) => ({ ...p, category: v }))} /></Field>
                <Field label="Genre"><Chips testid="ob-gender" options={["homme", "femme"]} value={[f.gender]} onToggle={(v) => setF((p) => ({ ...p, gender: v }))} /></Field>
              </div>
            </>
          )}

          {step === 1 && (
            <>
              <div className="grid grid-cols-3 gap-4">
                <Field label="PB 1km"><Input data-testid="ob-pb1" placeholder="3:45" value={f.pb_1km} onChange={set("pb_1km")} className="h-12 text-base bg-secondary border-border" /></Field>
                <Field label="PB 5km"><Input data-testid="ob-pb5" placeholder="20:30" value={f.pb_5km} onChange={set("pb_5km")} className="h-12 text-base bg-secondary border-border" /></Field>
                <Field label="PB 10km"><Input data-testid="ob-pb10" placeholder="43:00" value={f.pb_10km} onChange={set("pb_10km")} className="h-12 text-base bg-secondary border-border" /></Field>
              </div>
              <Field label="Volume course actuel (km/sem)"><Input data-testid="ob-volume" type="number" min="0" value={f.weekly_run_km} onChange={setNum("weekly_run_km")} className="h-12 text-base bg-secondary border-border" /></Field>
              <Field label="Stations les plus faibles"><Chips testid="ob-stations" options={STATIONS} value={f.weak_stations} onToggle={(v) => toggle("weak_stations", v)} /></Field>
              <div className="grid grid-cols-2 gap-4">
                <Field label="Wall balls d'affilée (nb max sans pause)"><Input data-testid="ob-wb-reps" type="number" min="0" value={f.wall_balls_unbroken} onChange={setNum("wall_balls_unbroken")} className="h-12 text-base bg-secondary border-border" /></Field>
                <Field label="Poids wall balls"><Input data-testid="ob-wb-weight" placeholder="9kg" value={f.wall_balls_weight} onChange={set("wall_balls_weight")} className="h-12 text-base bg-secondary border-border" /></Field>
              </div>
            </>
          )}

          {step === 2 && (
            <>
              <Field label="Jours & plages horaires disponibles">
                <p className="text-xs text-muted-foreground -mt-1 mb-3">Sélectionne tes jours, puis définis 1 ou 2 plages horaires précises par jour. Le programme calera tes séances sur ces créneaux.</p>
                <div className="space-y-2" data-testid="ob-availability">
                  {DAYS.map((day) => {
                    const entry = f.availability.find((a) => a.day === day);
                    const active = !!entry;
                    return (
                      <div key={day} className={`rounded-md border p-3 transition-colors ${active ? "border-primary/50 bg-secondary/60" : "border-border bg-secondary/30"}`}>
                        <div className="flex items-center justify-between">
                          <button type="button" data-testid={`ob-day-${day}`} onClick={() => toggleDay(day)} className="flex items-center gap-2.5 text-sm font-semibold active:scale-95 transition-transform">
                            <span className={`w-5 h-5 rounded flex items-center justify-center border ${active ? "bg-primary border-primary" : "border-border"}`}>
                              {active && <Check size={14} className="text-white" />}
                            </span>
                            {day}
                          </button>
                          {active && entry.ranges.length < 2 && (
                            <button type="button" data-testid={`ob-addrange-${day}`} onClick={() => addRange(day)} className="text-xs text-primary font-medium flex items-center gap-1 active:scale-95 transition-transform">
                              <Plus size={13} /> Ajouter une plage
                            </button>
                          )}
                        </div>
                        {active && (
                          <div className="mt-3 space-y-2">
                            {entry.ranges.map((r, idx) => (
                              <div key={idx} className="flex items-center gap-2">
                                <Input type="time" data-testid={`ob-range-${day}-${idx}-start`} value={r.start} onChange={(e) => updateRange(day, idx, "start", e.target.value)} className="h-11 text-base bg-background border-border flex-1 min-w-0" />
                                <span className="text-muted-foreground text-sm shrink-0">à</span>
                                <Input type="time" data-testid={`ob-range-${day}-${idx}-end`} value={r.end} onChange={(e) => updateRange(day, idx, "end", e.target.value)} className="h-11 text-base bg-background border-border flex-1 min-w-0" />
                                {entry.ranges.length > 1 && (
                                  <button type="button" data-testid={`ob-rmrange-${day}-${idx}`} onClick={() => removeRange(day, idx)} className="text-muted-foreground hover:text-primary shrink-0 p-1"><X size={16} /></button>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </Field>
              <Field label="Volume total dispo (heures/sem)"><Input data-testid="ob-hours" type="number" min="0" value={f.weekly_hours} onChange={setNum("weekly_hours")} className="h-12 text-base bg-secondary border-border" /></Field>
            </>
          )}

          {step === 3 && (
            <>
              <Field label="Type de régime">
                <Chips testid="ob-diettype" options={["omnivore", "végétarien", "végan", "sans porc", "pescétarien"]} value={[f.diet_type]} onToggle={(v) => setF((p) => ({ ...p, diet_type: v }))} />
              </Field>
              <Field label="Allergies / intolérances">
                <Chips testid="ob-allergies" options={ALLERGENS} value={f.allergies} onToggle={(v) => toggle("allergies", v)} />
              </Field>
              <Field label="Autre allergie / intolérance"><Input data-testid="ob-allergies-other" placeholder="ex : kiwi, sésame…" value={f.allergies_other} onChange={set("allergies_other")} className="h-12 text-base bg-secondary border-border" /></Field>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label="Aliments appréciés"><Input data-testid="ob-liked" placeholder="poulet, riz, avoine…" value={f.liked_foods} onChange={set("liked_foods")} className="h-12 text-base bg-secondary border-border" /></Field>
                <Field label="Aliments à éviter (goût)"><Input data-testid="ob-disliked" placeholder="brocoli, tofu…" value={f.disliked_foods} onChange={set("disliked_foods")} className="h-12 text-base bg-secondary border-border" /></Field>
              </div>
              <Field label="Supplémentation actuelle"><Input data-testid="ob-supps" placeholder="créatine, protéines…" value={f.supplements} onChange={set("supplements")} className="h-12 text-base bg-secondary border-border" /></Field>
              <Field label="Sommeil (h/nuit)"><Input data-testid="ob-sleep" type="number" min="0" value={f.sleep_hours} onChange={setNum("sleep_hours")} className="h-12 text-base bg-secondary border-border" /></Field>
              <Field label="Blessures / limitations"><Textarea data-testid="ob-injuries" value={f.injuries} onChange={set("injuries")} className="text-base bg-secondary border-border min-h-[80px]" placeholder="Aucune, ou décris…" /></Field>
              <Field label="Parle-moi de toi (optionnel)"><Textarea data-testid="ob-about" value={f.about_me} onChange={set("about_me")} className="text-base bg-secondary border-border min-h-[90px]" placeholder="Objectifs, contraintes de vie (famille, travail), appréhensions ou points d'attention physiques/mentaux. Ton coach IA en tiendra compte." /></Field>
            </>
          )}
        </div>

        <div className="flex gap-3 mt-6">
          {step > 0 && (
            <Button data-testid="ob-back-btn" variant="outline" onClick={() => setStep(step - 1)} className="h-13 py-3 px-6 rounded-md border-border bg-transparent hover:bg-secondary">
              <ChevronLeft size={18} /> Retour
            </Button>
          )}
          {step < 3 ? (
            <Button data-testid="ob-next-btn" onClick={() => setStep(step + 1)} className="flex-1 h-13 py-3 bg-primary hover:bg-primary/90 text-white rounded-md font-semibold active:scale-95 transition-transform">
              Continuer <ChevronRight size={18} />
            </Button>
          ) : (
            <Button data-testid="ob-submit-btn" onClick={submit} disabled={saving} className="flex-1 h-13 py-3 bg-primary hover:bg-primary/90 text-white rounded-md font-semibold active:scale-95 transition-transform">
              {saving ? "Enregistrement…" : "Générer mon programme"}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
